import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  SelectChangeEvent,
  Alert,
} from '@mui/material';
import { Modal, PayByLinkModal } from '@/types/modal';
import { useGetPayByLinkForm, useItem, useSubmitPayByLink } from '@/hooks/item';
import { LoadingBodyModal } from '@/components/molecules/common/Modal';
import PayByLinkDetail from './PayByLinkDetail';
import { getClientById } from '@/services/client';
import { useDebounce } from '@/hooks/common';

type PayByLinkForm = {
  category: string;
  paymentValue: string;
  buyerUserId?: string;
  orderId?: string;
};

// TODO:
// This should use common modal at src/components/molecules/common/Modal
// If we really need a customize modal we still need to place in common Modal Eg: AddressFormModal
// We have to reuse the common styling and behaviour Eg: title style, button style, data-cy...
const PayByLink: React.FC<{ modal: PayByLinkModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item } = modal;
  const itemRq = useItem(item.productId);
  const [form, setForm] = useState<PayByLinkForm>({
    category: '',
    paymentValue: '',
    buyerUserId: '',
    orderId: '',
  });
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [_modal, setModal] = useState<Modal | null>(null);
  const [isBuyerLoading, setIsBuyerLoading] = useState(false);

  const data = useGetPayByLinkForm(modal.item.productId);
  const submitMutation = useSubmitPayByLink(modal.item.productId);

  // Set first item as default when data is loaded
  useEffect(() => {
    if (data.kind === 'Loaded' && data?.data?.length > 0) {
      const firstItem = data.data[0];
      setForm((prev) => ({
        ...prev,
        category: firstItem.reason || '',
        paymentValue: firstItem.amount?.toString() === '0' ? '' : firstItem.amount?.toString() || '',
      }));
      setSelectedCurrency(firstItem.currency || '');
    }
  }, [data.kind]);

  // Clear submit error when form changes
  useEffect(() => {
    if (submitError) {
      setSubmitError('');
    }
  }, [form]);

  const handleChange =
    (field: keyof PayByLinkForm) => (event: SelectChangeEvent | React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));

      if (field === 'category') {
        setForm((prev) => ({ ...prev, [field]: value, buyerUserId: '' }));
        // Reset buyerUserId and get currency from reason data when switching away
        if (data.kind === 'Loaded') {
          const selectedReason = data.data.find((item) => item.reason === value);
          if (selectedReason) {
            setSelectedCurrency(selectedReason.currency || '');
            setForm((prev) => ({
              ...prev,
              [field]: value,
              paymentValue: selectedReason.amount?.toString() === '0' ? '' : selectedReason.amount?.toString() || '',
            }));
          } else {
            setSelectedCurrency(''); // Reset currency if no reason found
          }
        }
      }

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

  const lookupBuyer = useDebounce(async (id: string) => {
    const buyerId = Number(id);
    if (isNaN(buyerId)) {
      setErrors((prev) => ({ ...prev, buyerUserId: t('modal.pay_by_link.invalid_buyer_id') }));
      return;
    }

    setIsBuyerLoading(true);
    try {
      const result = await getClientById(buyerId);
      if (result.kind === 'Ok' && result.data.length > 0) {
        const buyer = result.data[0];
        setSelectedCurrency(buyer.currency || '');
        setErrors((prev) => ({ ...prev, buyerUserId: '' })); // Clear error if successful
      } else {
        setErrors((prev) => ({ ...prev, buyerUserId: t('modal.pay_by_link.buyer_not_found') }));
        setSelectedCurrency('');
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, buyerUserId: t('service_error.general') }));
      setSelectedCurrency('');
    } finally {
      setIsBuyerLoading(false);
    }
  }, 500);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!form.category) {
      newErrors.category = t('modal.pay_by_link.category_required');
    }

    if (form.category === 'high_value_order') {
      if (!form.buyerUserId) {
        newErrors.buyerUserId = t('modal.pay_by_link.buyer_id_required');
      } else {
        const buyerId = Number(form.buyerUserId);
        if (isNaN(buyerId) || buyerId <= 0) {
          newErrors.buyerUserId = t('modal.pay_by_link.invalid_buyer_id');
        }
      }

      if (!form.orderId) {
        newErrors.orderId = t('modal.pay_by_link.order_id_required');
      } else {
        const orderIdNum = Number(form.orderId);
        if (isNaN(orderIdNum) || orderIdNum <= 0) {
          newErrors.orderId = t('modal.pay_by_link.invalid_order_id');
        }
      }
    }

    if (!form.paymentValue) {
      newErrors.paymentValue = t('modal.pay_by_link.payment_value_required');
    } else {
      const value = parseFloat(form.paymentValue);
      if (isNaN(value) || value <= 0) {
        newErrors.paymentValue = t('modal.pay_by_link.invalid_payment_value');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      currency: selectedCurrency,
      amount: form.paymentValue,
      reason: form.category,
      buyerId: form.buyerUserId ? Number(form.buyerUserId) : undefined,
      orderId: form.orderId ? Number(form.orderId) : undefined,
    };

    submitMutation.mutate(payload);
  };

  //   If selected reason have amount !== 0, then disable input payment value
  const isAllowInputPaymentValue = useMemo(() => {
    if (data.kind === 'Loaded') {
      const selectedReason = data.data.find((item) => item.reason === form.category);
      return selectedReason?.amount?.toString() === '0';
    }
    return false;
  }, [data.kind, form.category]);

  useEffect(() => {
    if (submitMutation.kind === 'Error') {
      setSubmitError(submitMutation.error.message || t('modal.pay_by_link.submit_error'));
    } else if (submitMutation.kind === 'Completed') {
      itemRq.refetch();
      setModal({
        kind: 'PayByLinkDetail',
        item: modal.item,
        isViewDetail: false,
        onClose: () => {
          setModal(null);
          modal.onClose();
        },
      });
    }
  }, [submitMutation.kind]);

  return (
    <>
      <Dialog
        open={true}
        onClose={modal.onClose}
        maxWidth="sm"
        fullWidth
        data-cy="modal-pay-by-link pay-by-link"
      >
        <DialogTitle data-cy="modal-pay-by-link-title modal-title">{t('modal.pay_by_link.title')}</DialogTitle>
        <DialogContent>
          <div data-cy={'modal-body'}>
            {data.kind === 'Called' || submitMutation.kind === 'Loading' ? (
              <LoadingBodyModal />
            ) : data.kind === 'Error' ? (
              <Typography
                color="error"
                sx={{ p: 2 }}
              >
                {t('modal.pay_by_link.error')}
              </Typography>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    whiteSpace="pre-line"
                    data-cy="modal-pay-by-link-description"
                  >
                    {t('modal.pay_by_link.description', { itemId: modal.item.productId })}
                  </Typography>
                </Box>

                {submitError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    data-cy="modal-pay-by-link-error"
                  >
                    {submitError}
                  </Alert>
                )}

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.category}
                >
                  <InputLabel data-cy="modal-pay-by-link-category-label">
                    {t('modal.pay_by_link.category')} *
                  </InputLabel>
                  <Select
                    value={form.category}
                    onChange={handleChange('category')}
                    label={t('modal.pay_by_link.category')}
                    required
                    data-cy="modal-pay-by-link-category"
                  >
                    {data.data.map((item) => (
                      <MenuItem
                        key={item.reason}
                        value={item.reason || ''}
                      >
                        {item.reasonDisplayText}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category && (
                    <Typography
                      color="error"
                      variant="caption"
                    >
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>

                {form.category === 'high_value_order' && (
                  <>
                    <FormControl
                      fullWidth
                      margin="normal"
                      error={!!errors.buyerUserId}
                    >
                      <TextField
                        label={
                          <span data-cy="modal-pay-by-link-buyer-user-id-label">
                            {t('modal.pay_by_link.buyer_user_id')}
                          </span>
                        }
                        value={form.buyerUserId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          handleChange('buyerUserId')({ ...e, target: { ...e.target, value } });
                          if (value) {
                            lookupBuyer(value);
                          } else {
                            setSelectedCurrency('');
                            setErrors((prev) => ({ ...prev, buyerUserId: '' }));
                          }
                        }}
                        type="number"
                        disabled={isBuyerLoading}
                        required
                        error={!!errors.buyerUserId}
                        helperText={errors.buyerUserId}
                        inputProps={{ min: '1' }}
                        data-cy="modal-pay-by-link-buyer-user-id"
                      />
                    </FormControl>

                    <FormControl
                      fullWidth
                      margin="normal"
                      error={!!errors.orderId}
                    >
                      <TextField
                        label={
                          <span data-cy="modal-pay-by-link-order-id-label">{t('modal.pay_by_link.order_id')}</span>
                        }
                        value={form.orderId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          handleChange('orderId')({ ...e, target: { ...e.target, value } });
                        }}
                        type="number"
                        required
                        error={!!errors.orderId}
                        helperText={errors.orderId}
                        inputProps={{ min: '1' }}
                        data-cy="modal-pay-by-link-order-id"
                      />
                    </FormControl>
                  </>
                )}

                <FormControl
                  fullWidth
                  margin="normal"
                >
                  <TextField
                    label={<span data-cy="modal-pay-by-link-currency-label">{t('modal.pay_by_link.currency')}</span>}
                    value={selectedCurrency}
                    disabled
                    fullWidth
                    data-cy="modal-pay-by-link-currency"
                  />
                </FormControl>

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.paymentValue}
                >
                  <TextField
                    label={
                      <span data-cy="modal-pay-by-link-payment-value-label">
                        {t('modal.pay_by_link.payment_value')}
                      </span>
                    }
                    value={form.paymentValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('paymentValue')(e)}
                    type="number"
                    disabled={!isAllowInputPaymentValue}
                    inputProps={{ step: '0.01', min: '0' }}
                    required
                    error={!!errors.paymentValue}
                    helperText={errors.paymentValue}
                    data-cy="modal-pay-by-link-payment-value"
                  />
                </FormControl>
              </>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={modal.onClose}
            data-cy="modal-pay-by-link-cancel modal-action"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={data.kind !== 'Loaded' || submitMutation.kind === 'Loading'}
            data-cy="modal-pay-by-link-submit modal-action"
          >
            {t('common.submit')}
          </Button>
        </DialogActions>
      </Dialog>
      {_modal?.kind === 'PayByLinkDetail' && <PayByLinkDetail modal={_modal} />}
    </>
  );
};

export default PayByLink;
